// @givmo/app-admin — App root: providers + router.
//
// Composition order (outer → inner):
//   QueryClientProvider  — TanStack Query client at the root (07-1.0 §3).
//   AuthProvider         — tracks the live Firebase session.
//   RouterProvider       — receives the LIVE auth state and the query client via the `context` prop
//                          (the router itself was created with a placeholder context in
//                          createAdminRouter). A post-commit uid effect clears the cache and re-runs
//                          route guards.
//
// `App` takes its dependencies (auth instance, query client, router) as props so it can be rendered
// in tests with an in-memory router + a mock Firebase auth, with no real Firebase/network.

import { useLayoutEffect, useRef, useState } from 'react';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import type { Auth } from 'firebase/auth';
import type { SuiteSessionTransport } from '@givmo/auth';
import { AuthProvider, useAuth } from './auth/auth-context';
import { AuthSplash } from './components/auth-splash';
import { resetSignUpJoinStore } from './features/auth/sign-up-join-context';
import { resetSignUpDetailsStore } from './features/auth/sign-up-context';
import type { createAdminRouter } from './router/router';

export type AdminRouter = ReturnType<typeof createAdminRouter>;

export interface AppProps {
  auth: Auth;
  queryClient: QueryClient;
  router: AdminRouter; /**
   * The suite-session transport (AS-SUITE4), threaded to `AuthProvider`. `main.tsx` builds it;
   * omitted in tests, which disables the rail so the tree behaves exactly as it did before.
   */
  suiteSession?: SuiteSessionTransport;
}

/**
 * Inner tree (inside AuthProvider) so it can read the live auth state and feed it to the router.
 *
 * Gates the router on `isReady`: until the first `onAuthStateChanged` emission resolves, render the
 * splash and DO NOT mount `RouterProvider`. This closes the cold-load auth race — the `beforeLoad`
 * guard never runs against an unresolved session, so a hard refresh into a protected route with a
 * persisted session lands on that route (not /login-home), while a genuinely signed-out cold-load
 * still redirects to /login-home once ready.
 */
function RoutedApp({ router, queryClient }: { router: AdminRouter; queryClient: QueryClient }) {
  const auth = useAuth();
  const uid = auth.user?.uid ?? null;
  const previousUidRef = useRef(uid);
  /** Whether the router was mounted before the commit the effect below is running for. */
  const routerMountedRef = useRef(false);
  const [cacheEpoch, setCacheEpoch] = useState(0);

  useLayoutEffect(() => {
    const routerWasMounted = routerMountedRef.current;
    routerMountedRef.current = auth.isReady;
    if (previousUidRef.current === uid) return;
    const previousUid = previousUidRef.current;
    previousUidRef.current = uid;

    // EP-SUITE2-FIX7 ⚠ ONLY A ROUTER THAT WAS ALREADY MOUNTED HAS ANYTHING TO RESET. On a full page
    // load the SDK restores a persisted session AFTER the first render, so that first emission changes
    // the uid in the very commit that first mounts the router — whose own first load already runs
    // against this identity, over an empty cache. Treating it as a transition cleared, invalidated and
    // remounted that router mid-load: two more loads of the same location behind the first, and the
    // first load's reads cancelled. The `/sign-up` door's async redirect then landed on the chooser the
    // first load had already rendered, which TanStack Router cannot suspend — a blank page.
    if (!routerWasMounted) return;

    // Firebase identity is a hard cache boundary: admin query keys are not scoped by uid. This runs
    // after RouterProvider receives the new auth context, so invalidation also makes external/session
    // expiry sign-outs re-run guards against the committed user state.
    queryClient.clear();
    // Clearing destroys the cache, but an already-mounted QueryObserver can retain its last result.
    // Remount route consumers before paint so no prior admin's company data survives a uid switch.
    // ⚠ That remount is why the sign-up join store lives OUTSIDE the router tree (module scope,
    // `sign-up-join-context.tsx`): the email sign-up creates the Firebase user MID-FLOW, and a store
    // inside the tree would be rebuilt empty between the pivot's write and the next screen's read.
    // The boundary still owns its lifecycle — every transition AWAY from a signed-in uid (a sign-out,
    // or one account replacing another in this tab) clears the pivot target, so the next identity
    // never inherits one it did not resolve. The null→A sign-in does not clear it: the sign-up's own
    // transition, and the store is written after it.
    // The sign-up details store follows the same rule (NF-FIX21: the code screen reads them after the
    // email sign-up's own null→A transition, so that transition must not clear them).
    if (previousUid !== null) {
      resetSignUpJoinStore();
      resetSignUpDetailsStore();
    }
    setCacheEpoch((epoch) => epoch + 1);
    void router.invalidate();
  }, [auth.isReady, queryClient, router, uid]);

  if (!auth.isReady) {
    return <AuthSplash />;
  }
  return <RouterProvider key={cacheEpoch} router={router} context={{ auth, queryClient }} />;
}

export function App({ auth, queryClient, router, suiteSession }: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider auth={auth} suiteSession={suiteSession}>
        <RoutedApp router={router} queryClient={queryClient} />
      </AuthProvider>
    </QueryClientProvider>
  );
}
